import { Box, Grid } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import {
  CardComponent,
  PageTitleAndSubTitle,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import { observer } from 'mobx-react';
import React, { ChangeEvent, useEffect, useState } from 'react';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import { useNavigate } from 'react-router-dom';
import { DOC_COMP_INTEGRAL, NEW_DOC_COMPARISON } from '../../core-utils/routes';
import ComparisonTable from '../../_components/atoms/ComparisonTable';
import properties from '../../core-utils/properties';
import axios from 'axios';
import { DueDiligenceDataType } from '../../core-utils/helper-utils/recommendedAppsHelper';
import { RECOMMENDED_APPS } from '../../core-utils/messages/RecommendedAppConstants';
import Loader from '../../_components/molecules/Loader';
import { Pagination } from '@mui/material';
import {
  COMP_LIB_TABLE_COLUMNS,
  formatComparisonTableData,
} from '../../core-utils/helper-utils/docComparisonHelper';
import NoDoc from '../../_components/atoms/Icons/NoDocuments';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import { compilationDataProps } from '../TermCompilerPage';
import { useStores } from '../../stores';

export interface Document {
  document_name: string;
}
export interface ComparisonDataPros {
  _id: string;
  comparison_name?: string;
  vendor_type?: string;
  contract_ids?: string[];
  comparison_parameters?: any[];
  status:
    | 'Ready'
    | 'Failed Compilation'
    | 'Processing'
    | 'No Documents Uploaded';
  createdAt: string;
  documents: Document[];
}

const useStyles = makeStyles()({
  root: {
    display: 'flex',
    flexDirection: 'column',
  },
  titleAndSubtitle: {
    width: '1276px',
    padding: '32px 0px 32px 63px',
  },
  hr: {
    borderBottom: `1px solid ${patientAccessTheme.palette.grey[300]}`,
  },
  body: {
    width: '1276px',
    padding: '40px 0px 32px 63px',
  },
  pagination: {
    display: 'flex',
    justifyContent: 'flex-end',
  },
  noDataGrid: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
});

const DocumentComparisonPage: React.FC<{}> = observer(({}) => {
  // Constants
  const { classes } = useStyles();
  const { TITLE, SUBTITLE, card } = RECOMMENDED_APPS.DOC_COMPARISON;
  const navigate = useNavigate();
  const pageLimit = 10;
  const loginPageStore = useStores().loginPageStore;

  const [allComparisonTitles, setAllComparisonTitles] = useState<string[]>([]);

  // States
  const [comparisonData, setComparisonData] = useState<ComparisonDataPros[]>(
    [],
  );
  const [mainLoading, setMainLoading] = useState(true);
  const [pageCount, setPageCount] = useState(10);
  const [tablePageNumber, setTablePageNumber] = useState(1);

  // Functions
  const handlePagination = (_event: ChangeEvent<unknown>, page: number) => {
    setTablePageNumber(page);
  };

  const handleRowClick = (
    eg_nid: DueDiligenceDataType['eg_nid'],
    status: DueDiligenceDataType['status'] | compilationDataProps['status'],
    contract_ids: ComparisonDataPros['contract_ids'],
    comparison_name: ComparisonDataPros['comparison_name'],
    vendor_type: ComparisonDataPros['vendor_type'],
    comparison_id: ComparisonDataPros['_id'],
  ) => {
    if (status === 'Ready') {
      navigate(DOC_COMP_INTEGRAL, {
        state: {
          comparison_name,
          vendor_type,
          doc_comparison_id: comparison_id,
        },
      });
    }
  };

  const handleRegenerate = async (row: ComparisonDataPros) => {
    try {
      setComparisonData((prev) =>
        prev.map((r) =>
          r._id === row._id ? { ...r, status: 'Processing' } : r,
        ),
      );

      await axios.post(
        `${properties.aiqBaseUrl}${properties.contractBaseUrl}${properties.regenerateDocComparison}/${row._id}`,
      );

      const maxRetries = 20;
      const delay = 3000;
      let retries = 0;

      while (retries < maxRetries) {
        const response = await axios.post(
          `${properties.aiqBaseUrl}${properties.contractBaseUrl}${properties.getAllComparison}`,
          { page: tablePageNumber, limit: pageLimit },
        );

        const data: ComparisonDataPros[] = response?.data?.data || [];
        const comparison_names = response?.data?.comparison_names || [];
        setAllComparisonTitles(comparison_names);

        const formattedData = formatComparisonTableData(data);
        setComparisonData(formattedData);

        const updatedRow = data.find((r) => r._id === row._id);
        if (updatedRow?.status === 'Ready') {
          break;
        }

        await new Promise((res) => setTimeout(res, delay));
        retries++;
      }

      if (retries === maxRetries) {
        console.warn('Regenerate did not finish within expected time');
      }
    } catch (error) {
      console.error('Regenerate failed:', error);
    }
  };

  // Apis
  const getAllComparison = async () => {
    try {
      setMainLoading(true);
      const response = await axios.post(
        properties.aiqBaseUrl +
          properties.contractBaseUrl +
          properties.getAllComparison,
        {
          ...(loginPageStore?.isAdmin
            ? { eg_nid: loginPageStore.employerGroups }
            : {}),
          page: tablePageNumber,
          limit: pageLimit,
          eg_nids: loginPageStore?.isAdmin
            ? loginPageStore?.employerGroups
            : [],
        },
      );
      const comparisonData = response?.data?.data || [];
      const formattedData = formatComparisonTableData(comparisonData);
      setComparisonData(formattedData);

      const paginationData = response?.data?.pagination || {};
      setPageCount(paginationData?.total_pages || 0);

      const comparison_names = response?.data?.comparison_names || [];
      setAllComparisonTitles(comparison_names);
    } catch (err) {
    } finally {
      setMainLoading(false);
    }
  };

  // UseEffects
  useEffect(() => {
    getAllComparison();
  }, [tablePageNumber]);

  return (
    <Grid container className={classes.root}>
      <Grid className={classes.titleAndSubtitle}>
        <PageTitleAndSubTitle
          title={TITLE}
          subTitle={SUBTITLE}
          customButton={true}
          customButtonText="New Comparison"
          customButtonClick={() => {
            navigate(NEW_DOC_COMPARISON, {
              state: {
                comparison_names: allComparisonTitles,
              },
            });
          }}
          customButtonWidth="150px"
          customButtonVariant={'contained'}
        />
      </Grid>
      <div className={classes.hr} />
      <Grid className={classes.body}>
        <CardComponent
          title={
            <Typography
              fontSize="14px"
              fontType="semiBold"
              textColor={patientAccessTheme.palette.text.highemphasis}
            >
              {card.title}
            </Typography>
          }
          subTitle={card.subTitle}
          body={
            <Grid
              style={{
                marginTop: '10px',
              }}
            >
              {mainLoading ? (
                <Loader height="300px" />
              ) : comparisonData?.length > 0 ? (
                <Grid>
                  <ComparisonTable
                    columns={COMP_LIB_TABLE_COLUMNS}
                    rowsData={comparisonData}
                    handleRowClick={handleRowClick}
                    onRegenerate={handleRegenerate}
                  />
                  <Box className={classes.pagination}>
                    <Pagination
                      count={pageCount}
                      page={tablePageNumber}
                      boundaryCount={1}
                      color="primary"
                      size="small"
                      shape="circular"
                      onChange={handlePagination}
                    />
                  </Box>
                </Grid>
              ) : (
                <Grid className={classes.noDataGrid}>
                  <NoDoc />
                  <Typography
                    fontSize="16px"
                    fontType="semiBold"
                    textColor={patientAccessTheme.palette.text.highemphasis}
                    margin="16px 0px 10px 0px"
                  >
                    {card.noData.title}
                  </Typography>
                  <Typography
                    fontSize="14px"
                    fontType="regular"
                    textColor={patientAccessTheme.palette.text.mediumemphasis}
                  >
                    {card.noData.subTitle}
                  </Typography>
                </Grid>
              )}
            </Grid>
          }
        />
      </Grid>
    </Grid>
  );
});

export default DocumentComparisonPage;
